import { useState, useEffect } from 'react';

export interface ExchangeRates {
  [key: string]: number;
}

export interface Currency {
  code: string;
  symbol: string;
  name: string;
  format: (amount: number) => string;
}

export const CURRENCIES: Currency[] = [
  { 
    code: 'USD', 
    symbol: '$', 
    name: 'US Dollar',
    format: (amount) => `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  },
  { 
    code: 'EUR', 
    symbol: '€', 
    name: 'Euro',
    format: (amount) => `€${amount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
  },
  { 
    code: 'GBP', 
    symbol: '£', 
    name: 'British Pound',
    format: (amount) => `£${amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
  },
  { 
    code: 'JPY', 
    symbol: '¥', 
    name: 'Japanese Yen',
    format: (amount) => `¥${Math.round(amount).toLocaleString('ja-JP')}` 
  },
  { 
    code: 'CNY', 
    symbol: '¥', 
    name: 'Chinese Yuan',
    format: (amount) => `¥${amount.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
  },
  {
    code: 'AUD',
    symbol: 'A$',
    name: 'Australian Dollar',
    format: (amount) => `A$${amount.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  },
  {
    code: 'CAD',
    symbol: 'C$',
    name: 'Canadian Dollar',
    format: (amount) => `C$${amount.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
];

// Default exchange rates as fallback
const DEFAULT_RATES: ExchangeRates = {
  USD: 1,
  EUR: 0.92,
  GBP: 0.79,
  JPY: 151.61,
  CNY: 7.23,
  AUD: 1.52,
  CAD: 1.35
};

export function getDefaultCurrency(): string {
  try {
    // Get user's locale
    const userLocale = navigator.language;
    
    // Map common locales to currency codes
    const localeToCurrency: { [key: string]: string } = {
      'en-US': 'USD',
      'en-GB': 'GBP',
      'en-AU': 'AUD',
      'en-CA': 'CAD',
      'ja': 'JPY',
      'ja-JP': 'JPY',
      'zh': 'CNY',
      'zh-CN': 'CNY',
      'de': 'EUR',
      'fr': 'EUR',
      'it': 'EUR',
      'es': 'EUR'
    };

    // Try to match the full locale first
    let currencyCode = localeToCurrency[userLocale];
    
    // If no match, try the language part only
    if (!currencyCode) {
      const language = userLocale.split('-')[0];
      currencyCode = localeToCurrency[language];
    }

    // If still no match or currency not supported, default to USD
    if (!currencyCode || !CURRENCIES.find(c => c.code === currencyCode)) {
      return 'USD';
    }

    return currencyCode;
  } catch (error) {
    console.warn('Error detecting local currency:', error);
    return 'USD';
  }
}

export function useExchangeRates() {
  const [rates, setRates] = useState<ExchangeRates>(DEFAULT_RATES);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchRates = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD', {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.rates) {
        throw new Error('Invalid response format');
      }

      const newRates = {
        USD: 1,
        ...Object.fromEntries(
          Object.entries(data.rates).filter(([code]) => 
            CURRENCIES.some(c => c.code === code)
          )
        )
      };

      setRates(newRates);
      setLastUpdated(new Date());
      setError(null);

      // Store rates in localStorage with timestamp
      localStorage.setItem('exchangeRates', JSON.stringify({
        rates: newRates,
        timestamp: Date.now()
      }));
    } catch (err: any) {
      console.warn('Exchange rates fetch failed, using cached or default rates:', err.message);
      setError(err.message || 'Failed to fetch exchange rates');
      
      // Try to load cached rates from localStorage
      const cached = localStorage.getItem('exchangeRates');
      if (cached) {
        const { rates: cachedRates, timestamp } = JSON.parse(cached);
        const cacheAge = Date.now() - timestamp;
        
        // Use cached rates if less than 24 hours old
        if (cacheAge < 24 * 60 * 60 * 1000) {
          setRates(cachedRates);
          setLastUpdated(new Date(timestamp));
          return;
        }
      }
      
      // Fallback to default rates if no valid cache
      if (!lastUpdated) {
        setRates(DEFAULT_RATES);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRates();

    // Update rates every hour
    const interval = setInterval(fetchRates, 60 * 60 * 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  // Expose a refresh function to manually trigger updates
  const refresh = () => {
    setIsLoading(true);
    return fetchRates();
  };

  return { 
    rates, 
    isLoading, 
    error, 
    refresh,
    lastUpdated 
  };
}

export const formatCurrency = (amount: number, currencyCode: string, rates: ExchangeRates): string => {
  if (!amount || !currencyCode || !rates) return CURRENCIES[0].format(0);

  const currency = CURRENCIES.find(c => c.code === currencyCode);
  if (!currency) return CURRENCIES[0].format(amount);

  const rate = rates[currencyCode] || DEFAULT_RATES[currencyCode] || 1;
  const convertedAmount = amount * rate;

  return currency.format(convertedAmount);
};

export const getCurrencySymbol = (currencyCode: string): string => {
  const currency = CURRENCIES.find(c => c.code === currencyCode);
  return currency?.symbol || '$';
};